import React, { ChangeEvent, FormEvent, useCallback, useMemo } from 'react';
import Icon from 'icons/Icon';
import CustomTextArea from './CustomTextArea';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from './FormicaSearchSelectBox';
import { sarFieldInfoDictionary } from '../sar/config/SarFieldInfoConfiguration';
import { resolveDefinition } from '../sar/useSarFieldInfoConfiguration';
import { AccordionOpenCloseIcon } from './AccordionIndicators';
import CustomCalendar from './CustomCalendar';
import useDateField from '../sar/useDateField';
import FieldInfoButton from '../sar/FieldInfoButton';
import SarDefaultInfoButton from '../sar/components/SarDefaultInfoButton';

export interface EditableFieldRowProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  className?: string;
  hasError?: boolean;
  infoConfig?: {
    path: string;
    type?: string;
    additionalInfo?: string;
    mode?: 'bubble' | 'modal';
  };
  customOptions?: FormicaSelectBoxItem[];
}

const EditableFieldRow = ({
  label,
  value,
  onChange,
  multiline = false,
  className,
  hasError = false,
  infoConfig,
  customOptions,
}: EditableFieldRowProps): JSX.Element => {
  const handleInputChange = useCallback(
    (event: FormEvent<HTMLInputElement>): void => {
      onChange(event.currentTarget.value);
    },
    [onChange],
  );

  const handleTextAreaChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>): void => {
      onChange(event.currentTarget.value);
    },
    [onChange],
  );

  const fieldDefinition = useMemo(() => {
    if (!infoConfig) {
      return undefined;
    }
    return resolveDefinition(sarFieldInfoDictionary, infoConfig.path, label, infoConfig.type ?? 'Metin', infoConfig.additionalInfo);
  }, [infoConfig, label]);

  const selectOptions = useMemo(() => {
    if (customOptions && customOptions.length > 0) {
      return customOptions;
    }
    if (!fieldDefinition) {
      return [];
    }
    const isIletisimBilgisi = infoConfig?.path?.toLowerCase().includes('iletisimbilgisi');
    return extractCodeOptions(fieldDefinition.sections, isIletisimBilgisi);
  }, [fieldDefinition, infoConfig?.path, customOptions]);

  const shouldUseSelectBox = selectOptions.length > 0;
  const isDateField = infoConfig?.type?.toLowerCase() === 'tarih';

  const handleSelectChange = useCallback(
    (item: FormicaSelectBoxItem): void => {
      onChange(item.value);
    },
    [onChange],
  );

  const dateField = useDateField(isDateField ? value : '', onChange);

  return (
    <div className={`flex justify-between items-center pl-10 2xl:pl-16 pr-5 2x:pr-6 min-h-[52px] 2xl:min-h-[54px] h-fit py-2 bg-white w-full ${className}`}>
      <div className={'flex items-center gap-x-2'}>
        <AccordionOpenCloseIcon expanded={false} hasError={hasError} />
        <div className={'flex flex-col'}>
          <div className='flex items-center gap-x-2'>
            <span className={`text-xs tracking-wide text-f-black-2 ${hasError ? 'text-f-red-1' : ''}`}>Information</span>
            {infoConfig ? (
              <FieldInfoButton label={label} infoConfig={infoConfig} hasError={hasError} />
            ) : (
              <SarDefaultInfoButton hasError={hasError} onStopPropagation={(event) => event.stopPropagation()} />
            )}
          </div>
          <span className={`text-sm font-medium text-[#1D2939] ${hasError ? 'text-f-red-1' : ''}`}>{label}</span>
        </div>
      </div>
      <div className='flex items-center gap-x-2 min-w-fit max-w-[200px] w-full relative' ref={isDateField ? dateField.calendarRef : undefined}>
        <div className='flex-1 flex items-center relative'>
          {shouldUseSelectBox ? (
            <FormicaSearchSelectBox
              value={value}
              options={selectOptions}
              placeholder={label}
              onChange={handleSelectChange}
              ghost
              rounded
              className={hasError ? 'placeholder:!text-f-red-1 !text-f-red-1' : ''}
              selectBoxClassName={hasError ? 'border-f-red-1 border' : ''}
              customSize='medium'
            />
          ) : isDateField ? (
            <>
              <div className='flex items-center w-full relative'>
                <input
                  className={`w-full text-right focus:outline-none h-[34px] min-h-[34px] 2xl:h-[36px] 2xl:min-h-[36px] text-sm indent-1 text-f-black-2 bg-white pr-8 border border-[#E4E7EC] rounded-[6px] ${hasError ? 'border-f-red-1 border placeholder:text-f-red-1 text-f-red-1' : ''}`}
                  value={value}
                  onChange={dateField.handleDateInputChange}
                  onFocus={dateField.handleDateInputClick}
                  placeholder={`${label} Giriniz... (GG/AA/YYYY)`}
                />
                <div className='absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer' onClick={dateField.handleDateInputClick}>
                  <Icon
                    icon={'calendar'}
                    className={`text-f-black-2 hover:opacity-50 opacity-100 transition-opacity duration-200 ${hasError ? 'text-f-red-1' : ''}`}
                    size={16}
                  />
                </div>
              </div>
              {dateField.calendarVisible && (
                <div className='absolute right-0 top-full mt-1 z-50 bg-white border border-[#E4E7EC] rounded-[6px] shadow-lg'>
                  <CustomCalendar value={dateField.dateValue} onChange={dateField.handleDateChange} dateFormat='dd/mm/yyyy' showIcon={false} inline />
                </div>
              )}
            </>
          ) : multiline ? (
            <CustomTextArea
              className={`border border-[#E4E7EC] rounded-[6px] min-h-[34px] text-sm text-[#1D2939] ${hasError ? 'border-f-red-1 border placeholder:text-f-red-1 text-f-red-1' : ''}`}
              value={value}
              onChange={handleTextAreaChange}
              placeholder={label}
            />
          ) : (
            <input
              className={`w-full text-right focus:outline-none h-[34px] min-h-[34px] 2xl:h-[36px] 2xl:min-h-[36px] text-sm indent-1 text-f-black-2 bg-white pr-2 ${hasError ? 'border-f-red-1 border rounded-[4px] placeholder:text-f-red-1 text-f-red-1' : ''}`}
              value={value}
              onChange={handleInputChange}
              placeholder={`${label} Giriniz...`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const extractCodeOptions = (definitionSections?: { id: string; description: string }[], showOnlyCode: boolean = false): FormicaSelectBoxItem[] => {
  if (!definitionSections) {
    return [];
  }

  for (const section of definitionSections) {
    if (!section.description.includes('|')) {
      continue;
    }
    const options = section.description
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.includes('|'))
      .map((line) => {
        const [rawValue, rawLabel] = line.split('|');
        const valueText = rawValue?.replace(/^[-•\s]+/, '').trim();
        const labelText = rawLabel?.trim();
        if (!valueText || !labelText || valueText.toLowerCase().startsWith('kod')) {
          return undefined;
        }
        const label = showOnlyCode ? valueText : `${valueText} ${labelText}`;
        return { value: valueText, label };
      })
      .filter((item): item is FormicaSelectBoxItem => Boolean(item));
    if (options.length > 0) {
      return options;
    }
  }
  return [];
};
export default EditableFieldRow;
